"use client";

import { useState } from "react";
import { TextAreaField } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { WORD_LADDER_GENERATOR_STRINGS } from "@/components/tools/word-ladder-generator";
import { t } from "@/lib/i18n/interpolate";
import {
  checkLadder,
  type LadderIndex,
  parseWordInput,
  type StepStatus,
} from "@/lib/tool-kit/games-organizers/word-ladder";

type Strings = typeof WORD_LADDER_GENERATOR_STRINGS;

const STATUS_KEY: Record<StepStatus, keyof Strings["stepStatus"]> = {
  ok: "ok",
  "not-a-word": "notAWord",
  "bad-change": "badChange",
  repeated: "repeated",
};

/** A student's ladder, checked rung by rung against the word list. */
export default function LadderChecker({
  index,
  start,
  end,
  strings,
}: {
  index: LadderIndex;
  start: string;
  end: string;
  strings: Strings;
}) {
  const [text, setText] = useState("");
  const words = parseWordInput(text).filter((word) => word.length > 0);
  const check = words.length > 0 ? checkLadder(words, index, start, end) : null;
  let summary = "";
  if (check) {
    if (check.complete) {
      summary = t(strings.checkDone, { start, end });
    } else if (check.steps.every((step) => step.status === "ok")) {
      summary = t(strings.checkSoFar, { end });
    } else {
      summary = strings.checkFix;
    }
  }
  return (
    <section className="space-y-3 print:hidden">
      <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
      <TextAreaField
        hint={t(strings.checkHint, { start, end })}
        id="wl-check"
        label={strings.checkLabel}
        onChange={setText}
        placeholder={strings.checkPlaceholder}
        value={text}
      />
      {check ? (
        <div aria-live="polite" className="space-y-2">
          <p className="font-medium text-foreground text-sm">{summary}</p>
          <ul className="grid gap-1 text-sm sm:grid-cols-2">
            {check.steps.map((step, i) => (
              <li
                className={
                  step.status === "ok" ? "text-foreground" : "text-destructive"
                }
                key={`${i + 1}-${step.word}`}
              >
                <span className="font-mono">{step.word}</span>
                <span className={HINT}>
                  {" — "}
                  {strings.stepStatus[STATUS_KEY[step.status]]}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
